import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalizeAssetData, limitListingsSections } from '../util/data';
import * as log from '../util/log';
import { storableError } from '../util/errors';

// Pick paths from entries of appCdnAssets config (in configDefault.js)
const pickHostedConfigPaths = (assetEntries, excludeAssetNames) => {
  // E.g. allPaths = ['any/foo.json', 'any/bar.json']
  return assetEntries.reduce((pickedPaths, [name, path]) => {
    if (excludeAssetNames.includes(name)) {
      return pickedPaths;
    }
    return [...pickedPaths, path];
  }, []);
};
const getFirstAssetData = response => response?.data?.data[0]?.attributes?.data;
const getMultiAssetData = response => response?.data?.data;
const getMultiAssetIncluded = response => response?.data?.included;
const findJSONAsset = (assets, absolutePath) =>
  assets.find(a => a.type === 'jsonAsset' && a.attributes.assetPath === absolutePath);
const getAbsolutePath = path => (path.charAt(0) !== '/' ? `/${path}` : path);

const getGoogleAnalyticsId = (configAssets, path) => {
  if (!configAssets || !path) {
    return null;
  }
  const configAssetsData = getMultiAssetData(configAssets);
  const jsonAsset = findJSONAsset(configAssetsData, getAbsolutePath(path));
  const { enabled, measurementId } = jsonAsset?.attributes?.data?.googleAnalytics || {};
  return enabled ? measurementId : null;
};

// ================ Async Thunks ================ //

const fetchAppAssetsPayloadCreator = (arg, thunkAPI) => {
  const { assets, version } = arg;
  const { getState, extra: sdk, rejectWithValue } = thunkAPI;
  // App-wide assets include 2 content assets: translations for microcopy and footer
  const translationsPath = assets.translations;
  const footerPath = assets.footer;

  // The rest of the assets are considered as configurations
  const assetEntries = Object.entries(assets);
  const nonConfigAssets = ['translations', 'footer'];
  const configPaths = pickHostedConfigPaths(assetEntries, nonConfigAssets);

  // If version is given fetch assets by the version,
  // otherwise default to "latest" alias
  const fetchAssets = paths =>
    version
      ? sdk.assetsByVersion({ paths, version })
      : sdk.assetsByAlias({ paths, alias: 'latest' });

  const separateAssetFetches = [
    // This is a big file, better fetch it alone.
    // Then browser cache also comes into play.
    fetchAssets([translationsPath]),
    // Not a config, and potentially a big file.
    // It can benefit of browser cache when being a separate fetch.
    fetchAssets([footerPath]),
    // App configs
    fetchAssets(configPaths),
  ];

  return Promise.all(separateAssetFetches)
    .then(([translationAsset, footerAsset, configAssets]) => {
      const getVersionHash = response => response?.data?.meta?.version;
      const versionInTranslationsCall = getVersionHash(translationAsset);
      const versionInFooterCall = getVersionHash(footerAsset);
      const versionInConfigsCall = getVersionHash(configAssets);
      const hasSameVersions =
        versionInTranslationsCall === versionInFooterCall &&
        versionInFooterCall === versionInConfigsCall;

      // NOTE: making separate calls means that there might be version mismatch
      // when using 'latest' alias.
      // Since we only fetch translations and footer as a separate calls from configs,
      // there should not be major problems with this approach.
      // TODO: potentially show an error page or reload if version mismatch is detected.
      if (!version && !hasSameVersions) {
        console.warn("Asset versions between calls don't match.");
      }

      const googleAnalyticsId = getGoogleAnalyticsId(configAssets, assets.analytics);

      // Returned value looks like this for a single asset with name: "translations":
      // {
      //    translations: {
      //      path: 'content/translations.json', // an example path in Asset Delivery API
      //      data, // translation key & value pairs
      //    },
      // }

      /* ---------------- LOCALE RESOLUTION ---------------- */

      const state = getState();
      const { locale } = state.ui || {};
      const { currentUser } = state.user || {};
      const userLocale = currentUser?.attributes?.profile?.privateData?.language;
      const localStorageLocale = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
      const localeValue = localStorageLocale || userLocale || locale;

      /* --------------------------------------------------- */

      try {
        const fetchedConfigAssets = getMultiAssetData(configAssets);
        const included = getMultiAssetIncluded(configAssets);

        const collectedAssets = {};

        for (const [name, path] of assetEntries) {
          // Handle translations & footer
          if (nonConfigAssets.includes(name)) {
            const assetResponse = name === 'translations' ? translationAsset : footerAsset;
            let assetData = getFirstAssetData(assetResponse);

            if (name === 'footer') {
              try {
                const localModule = require(`../assets/content/pages/${localeValue}/${name}.json`);
                // Footer local file uses raw SDK array structure: { data: [{ attributes: { data } }] }
                const localData = localModule?.data?.[0]?.attributes?.data;
                if (localData) {
                  assetData = localData;
                }
              } catch (e) {
                // fallback to hosted footer
              }
            }

            collectedAssets[name] = {
              path,
              data: assetData,
            };
            continue;
          }

          // Hosted config asset
          const jsonAsset = findJSONAsset(fetchedConfigAssets, getAbsolutePath(path));

          let jsonAssetData = denormalizeAssetData({
            data: jsonAsset?.attributes?.data,
            included,
          });

          /* -------- LOCAL OVERRIDE FOR SPECIFIC CONFIGS -------- */
          if (name === 'topbar' || name === 'categories' || name === 'userFields' || name === 'listingFields' || name === 'listingTypes') {
            try {
              const localModule = require(`../assets/content/pages/${localeValue}/${name}.json`);
              if (localModule?.data) {
                jsonAssetData = localModule.data;
              }
            } catch (e) {
              // fallback to hosted config
            }
          }

          /* ---------------------------------------------------- */

          collectedAssets[name] = {
            path,
            data: jsonAssetData,
          };
        }

        return {
          assets: collectedAssets,
          version: versionInTranslationsCall,
          googleAnalyticsId,
        };
      } catch (e) {
        log.error(e, 'app-asset-fetch-failed', { assets, version });
        return rejectWithValue(storableError(e));
      }
    })
    .catch(e => {
      log.error(e, 'app-asset-fetch-failed', { assets, version });
      return rejectWithValue(storableError(e));
    });
};

const fetchAppAssetsThunk = createAsyncThunk(
  'hostedAssets/fetchAppAssets',
  fetchAppAssetsPayloadCreator
);
// Wrapper functions to maintain the same API as the original thunks without Redux Toolkit.
export const fetchAppAssets = (assets, version) => (dispatch, getState, sdk) => {
  return dispatch(fetchAppAssetsThunk({ assets, version }))
    .unwrap()
    .then(result => result?.assets || {});
};

// Webpack-safe local assets loader
const localAssets = require.context(
  '../assets/content/pages',
  true,
  /\.json$/
);

const fetchPageAssetsPayloadCreator = async (arg, thunkAPI) => {
  const { assets, hasFallback } = arg;
  const { getState, extra: sdk, rejectWithValue } = thunkAPI;

  const version = getState()?.hostedAssets?.version;
  if (typeof version === 'undefined') {
    throw new Error(
      'App-wide assets were not fetched first. Asset version missing from Redux store.'
    );
  }

  // If version is given fetch assets by the version,
  // otherwise default to "latest" alias
  const fetchAsset = version
    ? assetPath => sdk.assetByVersion({ path: assetPath, version })
    : assetPath => sdk.assetByAlias({ path: assetPath, alias: 'latest' });

  const assetEntries = Object.entries(assets);

  /* ---------------- LOCALE RESOLUTION ---------------- */

  const state = getState();
  const { locale: localeValue } = state.ui || {};
  const { currentUser } = state.user || {};
  const userLocale = currentUser?.attributes?.profile?.privateData?.language;
  const localStorageLocale = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
  const locale = userLocale || localStorageLocale || localeValue || 'id';

  /* --------------------------------------------------- */

  try {
    // Fetch remote assets in parallel
    const responses = await Promise.all(
      assetEntries.map(([_, path]) => fetchAsset(path))
    );

    const hostedAssetsState = getState()?.hostedAssets;

    // Keep fixed assets always in store
    const { termsOfService, privacyPolicy, landingPage, ...rest } =
      hostedAssetsState?.pageAssetsData || {};

    const fixedPageAssets = {
      termsOfService,
      privacyPolicy,
      landingPage,
    };

    // Prevent race-condition overwrites
    const pickLatestPageAssetData =
      hostedAssetsState?.currentPageAssets.reduce((acc, page) => {
        return rest[page] ? { ...acc, [page]: rest[page] } : acc;
      }, {}) || {};

    // Resolve assets (local → remote fallback)
    const resolvedAssets = await Promise.all(
      assetEntries.map(async ([name, path], index) => {
        let localData = null;
        const localPath = `./${locale}/${name}.json`;

        if (localAssets.keys().includes(localPath)) {
          localData = localAssets(localPath);
        }

        const contentData = localData || responses[index].data;
        const assetData = denormalizeAssetData(contentData);

        return [name, { path, data: assetData }];
      })
    );

    const pageAssets = {
      ...fixedPageAssets,
      ...pickLatestPageAssetData,
      ...Object.fromEntries(resolvedAssets),
    };

    return pageAssets;
  } catch (e) {
    if (hasFallback || (!hasFallback && e.status === 404)) {
      log.error(e, 'page-asset-fetch-failed', {
        assets,
        version,
        locale,
      });
    }
    return rejectWithValue(storableError(e));
  }
};

const fetchPageAssetsThunk = createAsyncThunk(
  'hostedAssets/fetchPageAssets',
  fetchPageAssetsPayloadCreator
);
// Wrapper functions to maintain the same API as the original thunks without Redux Toolkit.
export const fetchPageAssets = (assets, hasFallback) => (dispatch, getState, sdk) => {
  return dispatch(fetchPageAssetsThunk({ assets, hasFallback })).unwrap();
};

// ================ Slice ================ //

const hostedAssetsSlice = createSlice({
  name: 'hostedAssets',
  initialState: {
    // List of app-wide assets that should be fetched and their path in Asset API.
    // appAssets: { assetName: 'path/to/asset.json' }
    appAssets: {},
    pageAssetsData: null,
    currentPageAssets: [],
    // Current version of the saved asset.
    // Typically, the version that is returned by the "latest" alias.
    version: null,
    inProgress: false,
    error: null,
  },
  reducers: {
    setCurrentPageAssets: (state, action) => {
      state.currentPageAssets = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // fetchAppAssets cases
      .addCase(fetchAppAssetsThunk.pending, state => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(fetchAppAssetsThunk.fulfilled, (state, action) => {
        state.appAssets = action.payload.assets;
        state.version = state.version || action.payload.version;
        state.googleAnalyticsId = action.payload.googleAnalyticsId;
        state.inProgress = false;
      })
      .addCase(fetchAppAssetsThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      })
      // fetchPageAssets cases
      .addCase(fetchPageAssetsThunk.pending, (state, action) => {
        state.currentPageAssets = action.meta.arg.assets ? Object.keys(action.meta.arg.assets) : [];
        state.inProgress = true;
        state.error = null;
      })
      .addCase(fetchPageAssetsThunk.fulfilled, (state, action) => {
        state.pageAssetsData = action.payload;
        state.inProgress = false;
      })
      .addCase(fetchPageAssetsThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      });
  },
});

export const { setCurrentPageAssets } = hostedAssetsSlice.actions;
export default hostedAssetsSlice.reducer;
